import React from 'react';
import { useSelector } from 'react-redux';
import { shallowEqual } from 'react-redux';
import { RootReducer } from 'redux/reducers';
import { selectActiveDsMode, selectModeResolution } from 'redux/mode/reducers';
import { DesignSystemComponent } from 'designSystem/types';
import { ICON_PACKS, DEFAULT_ICON_PACK } from 'dragonmouse-shared/constants';
import { buildGoogleFontsCdnUrl, buildCustomFontsCss } from 'dragonmouse-shared/ds';
import { VITE_FORGE_URL } from '@constants';
import { useTheme } from 'styled-components';

interface ComponentPreviewProps {
	component: DesignSystemComponent;
	props: Record<string, any>; // Current prop values
	children?: string; // Children HTML to inject between component tags
	rawCode: {
		theme: string;
		styles: string;
		componentsCss: string;
		componentsJs: string;
	};
	thumbnail?: boolean; // Thumbnail mode: fixed size, scale to fit, no scrollbars
}

const ComponentPreview = ({
	component,
	props,
	children,
	rawCode,
	thumbnail = false,
}: ComponentPreviewProps) => {
	const iframeRef = React.useRef<HTMLIFrameElement>(null);
	const theme = useTheme();
	const { config, assets, activeDsMode, previewBg, isDragging } = useSelector(
		(r: RootReducer) => ({
			config: r.mode.designSystemContent?.config,
			assets: r.mode.designSystemContent?.assets?.all || [],
			activeDsMode: selectActiveDsMode(r.mode),
			previewBg: selectModeResolution(r.mode).previewBg,
			isDragging: r.mode.isDragging,
		}),
		shallowEqual
	);

	// Resize iframe to fit content after load (only in non-thumbnail mode)
	const onIframeLoad = () => {
		const iframe = iframeRef.current;
		if (!iframe?.contentDocument?.body) return;

		if (!thumbnail) {
			// In normal mode, resize iframe to fit content
			iframe.style.height = '0px';
			const height = iframe.contentDocument.documentElement.scrollHeight;
			iframe.style.height = `${height}px`;
		}
	};

	// Build attributes string for the component
	const buildAttributesString = () => {
		return Object.entries(props)
			.filter(([, value]) => value !== undefined && value !== null && value !== '')
			.map(([key, value]) => {
				const prop = component.props.find((p) => p.name === key);
				if (prop?.type === 'json') {
					try {
						// If value is already a string, use it directly; otherwise stringify
						const jsonStr = typeof value === 'string' ? value : JSON.stringify(value);
						return `${key}='${jsonStr.replace(/'/g, '&#39;')}'`;
					} catch {
						return '';
					}
				} else if (prop?.type === 'boolean') {
					return value ? key : '';
				} else {
					return `${key}="${String(value).replace(/"/g, '&quot;')}"`;
				}
			})
			.filter(Boolean)
			.join(' ');
	};

	// Check if we have the necessary code
	const hasComponents = rawCode.componentsJs && rawCode.componentsJs.trim().length > 0;

	// If no components.js, show error state
	if (!hasComponents) {
		return (
			<div
				style={{
					width: '100%',
					height: '100%',
					minHeight: '60px',
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					padding: '8px',
					fontSize: '10px',
					color: theme.orange[700],
					textAlign: 'center',
				}}
			>
				No components.js
			</div>
		);
	}

	const attributesStr = buildAttributesString();
	const componentHtml = `<${component.tagName}${attributesStr ? ' ' + attributesStr : ''}>${
		children || ''
	}</${component.tagName}>`;

	// Get icon pack configuration (use default if not specified or if pack is invalid/removed)
	const iconPack = config?.iconPack;
	const packId = iconPack && ICON_PACKS[iconPack] ? iconPack : DEFAULT_ICON_PACK;
	const packInfo = ICON_PACKS[packId];

	// Generate icon pack CDN include based on type (CSS link or JS script)
	const iconPackInclude =
		packInfo.type === 'css'
			? `<link rel="stylesheet" href="${packInfo.cdnLink}">`
			: `<script src="${packInfo.cdnLink}"></script>`;

	// Generate icon pack init script if needed (e.g., Lucide requires lucide.createIcons())
	const iconPackInit = packInfo.initScript ? `<script>${packInfo.initScript}</script>` : '';

	// Generate Google Fonts CDN link if fonts are configured
	const fonts = config?.fonts;
	const fontsCdnUrl = fonts && fonts.length > 0 ? buildGoogleFontsCdnUrl(fonts) : null;
	const fontsInclude = fontsCdnUrl
		? `<link rel="preconnect" href="https://fonts.googleapis.com">
	<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
	<link rel="stylesheet" href="${fontsCdnUrl}">`
		: '';

	// Generate @font-face rules for custom fonts
	const customFontsCss =
		fonts && fonts.length > 0 ? buildCustomFontsCss(fonts, `${VITE_FORGE_URL}/attachment`) : null;

	// Escape special chars to prevent malformed attributes in srcdoc
	const escapeAttr = (s: string) => s.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
	const dataModeAttr = activeDsMode ? ` data-mode="${escapeAttr(activeDsMode)}"` : '';

	// Replace asset paths with signed URLs
	let processedComponentsCss = rawCode.componentsCss || '';
	let processedComponentsJs = rawCode.componentsJs || '';
	let processedTheme = rawCode.theme || '';
	let processedStyles = rawCode.styles || '';

	if (assets && assets.length > 0) {
		assets.forEach((asset) => {
			const escapedPath = asset.path.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
			const regex = new RegExp(`(src=["']|href=["']|url\\(['"]?)\\/?${escapedPath}(["']|\\))`, 'g');
			const signedUrl = `${VITE_FORGE_URL}/attachment/${encodeURIComponent(asset.casHash)}`;

			processedComponentsCss = processedComponentsCss.replace(regex, `$1${signedUrl}$2`);
			processedComponentsJs = processedComponentsJs.replace(regex, `$1${signedUrl}$2`);
			processedTheme = processedTheme.replace(regex, `$1${signedUrl}$2`);
			processedStyles = processedStyles.replace(regex, `$1${signedUrl}$2`);
		});
	}

	// Build complete HTML document for iframe
	const fullHtml = `
<!DOCTYPE html>
<html lang="en"${dataModeAttr}>
<head>
	<meta charset="UTF-8">
	<meta name="viewport" content="width=device-width, initial-scale=1.0">
	<title>Component Preview</title>
	${fontsInclude}
	${iconPackInclude}
	${customFontsCss ? `<style>\n${customFontsCss}\n</style>` : ''}
	<style>
		${processedTheme}
	</style>
	<style>
		${processedStyles}
	</style>
	<style>
		${processedComponentsCss}
	</style>
	<style>
		/* Preview container styles */
		html, body {
			margin: 0;
			padding: 24px;
			font-family: var(--ds-font-sans, var(--ds-font-primary, system-ui, sans-serif));
			background: ${previewBg};
			color: ${theme.gray[900]};
		}
		body {
			display: flex;
			align-items: center;
			justify-content: center;
			min-height: auto;
			overflow: hidden;
		}
		/* Override fixed/sticky positioning for preview */
		.sidebar, .menu-toggle, .sidebar-mobile-overlay {
			position: relative !important;
			transform: none !important;
		}
		.sidebar-mobile-overlay {
			display: none !important;
		}
		/* Error display */
		.js-error {
			color: ${theme.red[500]};
			font-size: 12px;
			padding: 8px;
			background: ${theme.orange[50]};
			border-radius: 4px;
			font-family: monospace;
		}
	</style>
	<script>
		// Shared stylesheet for Shadow DOM components — lets child components inside
		// a shadow root use global DS classes (recipes, component CSS, icon fonts, etc.)
		(function() {
			var sheet = new CSSStyleSheet();
			var css = Array.from(document.querySelectorAll('head > style')).map(function(s) { return s.textContent; }).join('\\n');
			sheet.replaceSync(css);
			window.__DS_STYLES = sheet;
		})();
	</script>
</head>
<body>
	${componentHtml}
	<script>
		try {
			${processedComponentsJs}
		} catch (e) {
			document.body.innerHTML = '<div class="js-error">JS Error: ' + e.message + '</div>';
			console.error('Component script error:', e);
		}
	</script>
	${iconPackInit}
	<script>
		// Prevent all link clicks from navigating outside the preview iframe
		// srcDoc iframes resolve links relative to parent document
		document.addEventListener('click', function(e) {
			const anchor = e.target.closest('a');
			if (anchor) {
				e.preventDefault();
			}
		}, true);
	</script>
</body>
</html>
	`.trim();

	// Thumbnail mode: render iframe filling the container, content centered
	if (thumbnail) {
		return (
			<iframe
				ref={iframeRef}
				srcDoc={fullHtml}
				onLoad={onIframeLoad}
				style={{
					width: '100%',
					height: '100%',
					border: 'none',
					overflow: 'hidden',
					pointerEvents: 'none',
				}}
				title={`Preview of ${component.name}`}
				sandbox="allow-scripts allow-same-origin"
				scrolling="no"
			/>
		);
	}

	return (
		<iframe
			ref={iframeRef}
			srcDoc={fullHtml}
			onLoad={onIframeLoad}
			style={{
				width: '100%',
				height: '100px',
				border: 'none',
				background: 'transparent',
				pointerEvents: isDragging ? 'none' : 'auto',
			}}
			title={`Preview of ${component.name}`}
			sandbox="allow-scripts allow-same-origin"
		/>
	);
};

export default ComponentPreview;
